/**
 * Client-side persistence for flashcard spaced-repetition progress.
 *
 * There is no generic per-user key/value store on the backend (only
 * settings/metadata endpoints with fixed shapes), so progress is kept in
 * localStorage, namespaced per user so switching accounts doesn't bleed
 * state together. This is a pragmatic v1 choice — easy to swap for a real
 * endpoint later without touching the engine or the page.
 */
import { logger } from '@/shared/utils';
import type { ProgressMap } from './types';

const STORAGE_PREFIX = 'aditas_gmat_flashcards_progress_v1';

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}:${userId}`;
}

export function loadProgress(userId: string): ProgressMap {
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (!raw) return {};
    return JSON.parse(raw) as ProgressMap;
  } catch (error) {
    logger.error('Failed to load flashcard progress', 'flashcards.progressStorage', { error });
    return {};
  }
}

export function saveProgress(userId: string, progress: ProgressMap): void {
  try {
    window.localStorage.setItem(storageKey(userId), JSON.stringify(progress));
  } catch (error) {
    logger.error('Failed to save flashcard progress', 'flashcards.progressStorage', { error });
  }
}

export function resetProgress(userId: string): void {
  try {
    window.localStorage.removeItem(storageKey(userId));
  } catch (error) {
    logger.error('Failed to reset flashcard progress', 'flashcards.progressStorage', { error });
  }
}
